import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { checkout, getJwt, getMe, type CheckoutPlan, type Tier } from "../lib/api";
import { MBBtn, MBChip, MBEyebrow, MBIcon } from "../components/mb";

const MCP_URL = "https://mcpbuild.app/mcp";

const EXAMPLE_PROMPTS: Array<{ title: string; prompt: string; note?: string }> = [
  {
    title: "Bar chart",
    prompt:
      'Use dynamic_widget to render a bar chart of quarterly revenue. Pass this data: { quarters: [{label:"Q1",value:120},{label:"Q2",value:140},{label:"Q3",value:175},{label:"Q4",value:210}] }. Read it via useToolOutput(). Make the highest bar a brighter shade.',
  },
  {
    title: "Leaderboard with rank badges",
    prompt:
      'Use dynamic_widget for a leaderboard. Data: { rows: [{name:"Alice",score:1240},{name:"Bob",score:980},{name:"Carol",score:1450},{name:"Dan",score:720}] }. Sort by score desc, show 🥇🥈🥉 for the top 3, and a horizontal bar in each row proportional to the max score.',
  },
  {
    title: "Pomodoro timer (interactive)",
    prompt:
      "Use dynamic_widget for an interactive Pomodoro timer. 25-minute work session with Start / Pause / Reset buttons and a circular SVG progress ring around the time display. Use useState/useEffect. Honor useTheme() for dark mode.",
  },
  {
    title: "Sortable / filterable table",
    prompt:
      'Use dynamic_widget to render a table from useToolOutput() data. Data: { rows: [{id:1,name:"Widget A",price:24,status:"live"},{id:2,name:"Widget B",price:99,status:"draft"},{id:3,name:"Widget C",price:8,status:"live"}] }. Click a column header to sort, type in a filter box to narrow rows.',
  },
  {
    title: "Animated SVG hero",
    prompt:
      'Use dynamic_widget for an animated SVG: three concentric rings rotating at different speeds in deep purple and teal, with a centered title from useToolOutput().title. Data: { title: "Hello from MCPBuild" }. Use CSS keyframes; no external libs.',
  },
  {
    title: "Calendar heatmap",
    prompt:
      "Use dynamic_widget for a GitHub-style contributions heatmap. Generate 52 weeks × 7 days of cells (Sunday-first), each cell colored by `data.counts[i]` from useToolOutput(). Pass a `counts` array of 364 integers between 0 and 10. Cells with 0 are blank.",
    note: "Pass actual numbers if you want it filled; the LLM will fall back to random demo data otherwise.",
  },
  {
    title: "Multi-step form",
    prompt:
      "Use dynamic_widget for a 3-step signup form (step 1: email, step 2: company name, step 3: confirm). Show a progress indicator. On final submit, call await callTool('echo', { message: JSON.stringify(formValues) }) and display the echo result.",
  },
  {
    title: "Generate + describe as a tool",
    prompt:
      'Use dynamic_widget to render a weather card from useToolOutput() data: { city: "Boston", tempF: 54, condition: "Cloudy" }. ALSO fill in the helper fields so it can become a reusable tool: toolName "weather-card", toolDescription "Show a city\'s current weather. Call when the user asks about weather.", and inputSchema { type: "object", properties: { city: {type:"string"}, tempF: {type:"number"}, condition: {type:"string"} }, required: ["city"] }.',
    note: "The helper fields pre-fill the tool config when you add it to a project.",
  },
  {
    title: "Add a widget to a project (as a tool)",
    prompt:
      'Call list_widgets to find my most recent widget, then add_widget_to_project it into a new project called "Weather" — keep the suggested tool name and schema, and expose it.',
    note: "Creates the project if it doesn't exist. Then connect mcpbuild.app/mcp/weather in Claude to call it.",
  },
  {
    title: "Save it (one-step)",
    prompt:
      'Take that last widget and use save_widget_to_app to persist it under a project called "Examples" with tool slug "signup-form".',
    note: "Generates + saves to a project in one call (vs. add_widget_to_project, which reuses an existing Inbox widget).",
  },
  {
    title: "Data tool + widget that calls it",
    prompt:
      'Build a project called "Orders". First, project add_data_tool a tool called "cancel-order" with description "Cancel an order by its id. Returns {ok, orderId, cancelledAt}." inputSchema { type:"object", properties:{ orderId:{type:"string"} }, required:["orderId"] }, annotations { readOnlyHint: false }, and data { ok: true, cancelledAt: "2026-05-27T19:00:00Z" }. Then use dynamic_widget with projectName: "Orders" and toolSlug: "orders-table" to render an orders table with a "Cancel" button per row that, on click, calls await callTool(\'cancel-order\', { orderId: row.id }) and shows the result inline. Pass data: { rows: [{id:"A-100",customer:"Alice",status:"open"},{id:"A-101",customer:"Bob",status:"open"}] }.',
    note: "Shows the data-tool + callTool() bridge: button → data tool → result inline. `projectName` on dynamic_widget auto-saves into Orders (skips Inbox) so the widget is immediately callable on /mcp/orders.",
  },
  {
    title: "Data tool that's a pure lookup",
    prompt:
      'Add a data tool called "city-weather" to my "Weather" project. Description: "Look up a fictional city\'s current weather. Call when the user asks about a city by name." inputSchema { type:"object", properties:{ city:{type:"string"} }, required:["city"] }, annotations { readOnlyHint: true }, data { city:"Boston", tempF:54, condition:"Cloudy" }. Then connect mcpbuild.app/mcp/weather in Claude and call the tool.',
    note: "Pure JSON tool — no widget. Useful for lookups/calcs/mutations the LLM (or a widget button) can call.",
  },
  {
    title: "Remix a project (retheme it)",
    prompt:
      'Take my "Examples" project and project remix it into a new project called "Examples (Dark)" with accentColor "#7C3AED" and icon "🌙". Then use remix_widget on each cloned widget to apply a darker theme — deep purple accents, near-black backgrounds, and higher contrast type — keeping the existing logic and props intact. Show me the new project slug when done.',
    note: "Clones the whole project + all its widgets, then re-themes them in place. Source project is untouched.",
  },
];

// ── Layout helpers ───────────────────────────────────────────────────────────
function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <MBEyebrow style={{ marginBottom: 8 }}>{eyebrow}</MBEyebrow>
        <h2 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--fg-1)" }}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Card({ children, style = {} }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 12, padding: 18, ...style }}>
      {children}
    </div>
  );
}

function CopyableUrl({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "stretch", background: "var(--bg-0)", border: "1px solid var(--line-2)", borderRadius: 6, overflow: "hidden" }}>
        <code style={{ flex: 1, padding: "10px 12px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-1)", wordBreak: "break-all" }}>{url}</code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{
            padding: "0 14px",
            border: 0,
            borderLeft: "1px solid var(--line-2)",
            background: copied ? "var(--accent-soft)" : "var(--surface-2)",
            color: copied ? "var(--accent-fg)" : "var(--fg-1)",
            fontFamily: "var(--font-sans)",
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <MBIcon name={copied ? "check" : "copy"} size={12} />
          {copied ? "copied" : "copy"}
        </button>
      </div>
    </div>
  );
}

function PromptCard({ title, prompt, note }: { title: string; prompt: string; note?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Card style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ marginRight: "auto", fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>{title}</div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{ padding: "3px 9px", borderRadius: 4, border: "1px solid var(--line-2)", background: "transparent", color: "var(--fg-2)", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer", display: "inline-flex", gap: 5, alignItems: "center" }}
        >
          <MBIcon name={copied ? "check" : "copy"} size={11} />
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: "19px", color: "var(--fg-2)", whiteSpace: "pre-wrap" }}>{prompt}</pre>
      {note && <div style={{ marginTop: 8, fontFamily: "var(--font-sans)", fontSize: 11.5, color: "var(--fg-4)" }}>{note}</div>}
    </Card>
  );
}

// ── Billing ──────────────────────────────────────────────────────────────────
type Cycle = "monthly" | "yearly" | "lifetime";

const PRO_FEATURES = [
  "Unlimited projects",
  "Version history & restore",
  "Live data — bring your own API keys",
  "Publish public MCP endpoints + galleries",
  "Usage analytics",
  "Export any widget or project (.zip)",
];

function PlanButton({ plan, label }: { plan: CheckoutPlan; label: string }) {
  const [state, setState] = useState<"idle" | "redirecting" | "error">("idle");
  const [err, setErr] = useState("");
  const onClick = async () => {
    setState("redirecting");
    setErr("");
    try {
      const { url } = await checkout(plan);
      window.location.href = url;
    } catch (e) {
      setState("error");
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div style={{ width: "100%" }}>
      <MBBtn variant="primary" onClick={onClick} disabled={state === "redirecting"} style={{ width: "100%", justifyContent: "center" }}>
        {state === "redirecting" ? "Redirecting…" : label}
      </MBBtn>
      {state === "error" && <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--danger)" }}>{err}</div>}
    </div>
  );
}

function BillingSection() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const upgrade = params.get("upgrade");
  const [cycle, setCycle] = useState<Cycle>("monthly");

  const me = useQuery({ queryKey: ["me"], queryFn: getMe, enabled: !!getJwt(), retry: false });
  const tier: Tier = me.data?.tier ?? "free";

  useEffect(() => {
    if (upgrade === "success") {
      const t = setTimeout(() => qc.invalidateQueries({ queryKey: ["me"] }), 4000);
      return () => clearTimeout(t);
    }
  }, [upgrade, qc]);

  const dismiss = () => {
    params.delete("upgrade");
    setParams(params, { replace: true });
  };

  const proPlan: CheckoutPlan = cycle === "monthly" ? "pro_monthly" : cycle === "yearly" ? "pro_annual" : "lifetime";
  const proPrice = cycle === "monthly" ? "$8" : cycle === "yearly" ? "$79" : "$149";
  const proCadence = cycle === "monthly" ? "per month" : cycle === "yearly" ? "per year · ~$6.58/mo" : "one-time · yours forever";
  const proCta = cycle === "lifetime" ? "Get Lifetime — $149 once" : cycle === "yearly" ? "Go Pro — $79/yr" : "Go Pro — $8/mo";

  return (
    <Section eyebrow="Billing" title="Plan & billing">
      {upgrade === "success" && (
        <div style={{ borderRadius: 8, border: "1px solid var(--accent-line)", background: "var(--accent-soft)", padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, fontSize: 14 }}>
          <span style={{ flex: 1, color: "var(--fg-1)" }}>Payment received — your plan will activate within a few seconds. ✨</span>
          <button onClick={dismiss} style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", fontSize: 12 }}>dismiss</button>
        </div>
      )}
      {upgrade === "cancelled" && (
        <div style={{ borderRadius: 8, border: "1px solid var(--line-2)", background: "var(--surface-1)", padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, fontSize: 14 }}>
          <span style={{ flex: 1, color: "var(--fg-2)" }}>Checkout cancelled — no charge was made. You can upgrade anytime below.</span>
          <button onClick={dismiss} style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", fontSize: 12 }}>dismiss</button>
        </div>
      )}

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ marginRight: "auto", fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, color: "var(--fg-1)" }}>Current plan</div>
          <span
            style={{
              padding: "3px 9px",
              borderRadius: 4,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: tier === "free" ? "var(--fg-2)" : "var(--accent-fg)",
              background: tier === "free" ? "var(--surface-2)" : "var(--accent-soft)",
              border: `1px solid ${tier === "free" ? "var(--line-2)" : "var(--accent-line)"}`,
            }}
          >
            {tier}
          </span>
        </div>

        {tier === "lifetime" && (
          <p style={{ margin: "12px 0 0", color: "var(--fg-2)", fontSize: 14 }}>
            Lifetime — you're set forever ✨ Everything in Pro, no renewals. Thanks for backing MCPBuild early.
          </p>
        )}
        {tier === "pro" && (
          <p style={{ margin: "12px 0 0", color: "var(--fg-2)", fontSize: 14 }}>
            Pro — active. Unlimited projects, version history, exports, live data (bring your own API keys), public MCP endpoints, and usage analytics are all unlocked.
          </p>
        )}

        {tier === "free" && (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 14 }}>
              You're on Free — unlimited widget generation, the Inbox, and one connectable project. Upgrade for everything below.
            </p>

            {/* Billing-cycle toggle */}
            <div style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 4, padding: 4, background: "var(--bg-0)", border: "1px solid var(--line-2)", borderRadius: 8 }}>
              {([
                { k: "monthly", label: "Monthly" },
                { k: "yearly", label: "Yearly", badge: "save ~18%" },
                { k: "lifetime", label: "Lifetime", badge: "pay once", accent: true },
              ] as const).map((opt) => {
                const on = cycle === opt.k;
                return (
                  <button
                    key={opt.k}
                    onClick={() => setCycle(opt.k)}
                    style={{ padding: "7px 12px", background: on ? "var(--surface-3)" : "transparent", color: on ? "var(--fg-1)" : "var(--fg-3)", border: 0, borderRadius: 6, fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
                  >
                    {opt.label}
                    {"badge" in opt && opt.badge && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, padding: "2px 6px", background: "accent" in opt && opt.accent ? "var(--accent-soft)" : "var(--surface-2)", color: "accent" in opt && opt.accent ? "var(--accent-fg)" : "var(--fg-2)", border: `1px solid ${"accent" in opt && opt.accent ? "var(--accent-line)" : "var(--line-2)"}`, borderRadius: 4 }}>
                        {opt.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Pro panel */}
            <div style={{ borderRadius: 10, border: "1px solid var(--accent-line)", background: "var(--surface-2)", padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 600, color: "var(--fg-1)" }}>Pro</span>
                {cycle === "lifetime" && <MBChip accent>best value</MBChip>}
                <span style={{ marginLeft: "auto", fontFamily: "var(--font-sans)", fontSize: 32, fontWeight: 600, letterSpacing: "-0.03em", color: "var(--fg-1)", lineHeight: 1 }}>{proPrice}</span>
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-3)", marginTop: -8, textAlign: "right" }}>{proCadence}</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }} className="mb-pro-feats">
                {PRO_FEATURES.map((f) => (
                  <li key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--fg-2)" }}>
                    <MBIcon name="check" size={13} color="var(--accent)" />
                    {f}
                  </li>
                ))}
              </ul>
              <PlanButton plan={proPlan} label={proCta} />
            </div>
          </div>
        )}
      </Card>
    </Section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export function Settings() {
  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "32px 28px 96px", display: "flex", flexDirection: "column", gap: 36 }}>
      <h1 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 32, lineHeight: "36px", letterSpacing: "-0.02em", fontWeight: 600, color: "var(--fg-1)" }}>
        Settings
      </h1>

      <BillingSection />

      <Section eyebrow="Builder" title="Builder endpoint — the workshop">
        <Card style={{ borderColor: "var(--accent-line)", background: "var(--surface-2)" }}>
          <div style={{ fontFamily: "var(--font-sans)", fontSize: 13.5, lineHeight: "22px", color: "var(--fg-1)" }}>
            <strong style={{ color: "var(--fg-0)" }}>Not an engineer? Read this first.</strong>{" "}
            MCPBuild gives you <em>two</em> MCP URLs and they do different jobs:
            <ul style={{ margin: "10px 0 0", paddingLeft: 18, color: "var(--fg-2)" }}>
              <li>
                <strong style={{ color: "var(--fg-1)" }}>Workshop</strong> (the URL below): connect this in your
                day-to-day Claude/ChatGPT to <em>build</em> stuff. You'll say things like
                “build a project called Orders with a Cancel button” and the AI uses the workshop's tools to do it.
              </li>
              <li>
                <strong style={{ color: "var(--fg-1)" }}>Project</strong> (one per project, at{" "}
                <code style={{ fontFamily: "var(--font-mono)" }}>{MCP_URL}/&lt;your-project&gt;</code>): share this with
                whoever you're demoing to. It only exposes the tools that project actually offers — no build surface,
                no clutter. Add a few Demo prompts on a tool's <strong>Triggers</strong> tab so the audience can chat
                naturally and the model picks the right tool.
              </li>
            </ul>
          </div>
        </Card>
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 14, lineHeight: "22px" }}>
          Connect Claude (or any MCP client) to this URL to <em>build</em>:{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>dynamic_widget</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>list_widgets</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>add_widget_to_project</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>configure_project_tool</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>create_app</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>list_apps</code>. Auth is OAuth 2.1 + PKCE, reusing this
          site's sign-in — if you're logged in here, connecting is one click (no email).
        </p>
        <Card>
          <CopyableUrl label="Builder MCP URL" url={MCP_URL} />
        </Card>
      </Section>

      <Section eyebrow="Endpoints" title="Per-project endpoints">
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 14, lineHeight: "22px" }}>
          Every project also gets its own endpoint at{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>{MCP_URL}/&lt;project-slug&gt;</code>. Connecting one
          exposes <strong>only that project's tools</strong> (the ones toggled{" "}
          <span style={{ color: "var(--success)", fontFamily: "var(--font-mono)", fontSize: 12 }}>live</span>) — a focused
          MCP server per product or workspace. Use this for demos and for anyone using your project. The exact URL +
          connect instructions are on each project's page.
        </p>
      </Section>

      <Section eyebrow="Triggers" title="Make natural chat actually trigger your tools">
        <Card>
          <div style={{ color: "var(--fg-2)", fontSize: 14, lineHeight: "22px" }}>
            On any saved tool's page, open the <strong>Triggers</strong> tab and add a few sentences a real user would
            <em> say</em> to your demo audience — for example
            <code style={{ fontFamily: "var(--font-mono)", margin: "0 4px" }}>“cancel order A-100”</code>
            or
            <code style={{ fontFamily: "var(--font-mono)", margin: "0 4px" }}>“show me this week's pipeline”</code>.
            Two things happen:
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              <li>
                The phrases are appended to the tool's MCP description, so Claude/ChatGPT picks this tool when someone
                naturally describes the action — without you having to coach them on the tool name.
              </li>
              <li>
                Each phrase becomes a one-click <strong>Try this prompt</strong> pill on the same page, so you can copy
                a working prompt into a chat in front of an audience. Great for demos.
              </li>
            </ul>
            <div style={{ color: "var(--fg-3)", marginTop: 8, fontSize: 13 }}>
              Tip: write the sentence a non-technical user would say, not the tool name. 3-5 phrases usually covers it.
            </div>
          </div>
        </Card>
      </Section>

      <Section eyebrow="Get started" title="How to test in Claude Desktop">
        <Card>
          <ol style={{ margin: 0, paddingLeft: 20, color: "var(--fg-2)", fontSize: 14, lineHeight: "26px" }}>
            <li>Settings → Connectors → Add remote MCP server.</li>
            <li>Paste the Builder MCP URL above.</li>
            <li>Approve the sign-in prompt — same magic-link as this site.</li>
            <li>Pick a prompt from the list below and drop it into a chat.</li>
          </ol>
        </Card>
      </Section>

      <Section eyebrow="Prompts" title="Example prompts">
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 14, lineHeight: "22px" }}>
          Each card is a ready-to-paste prompt. Copy → paste into Claude or ChatGPT (with this MCP connected). The first
          call lands in the <strong>Inbox</strong>; use Save to project on the widget detail to keep it.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {EXAMPLE_PROMPTS.map((p) => (
            <PromptCard key={p.title} title={p.title} prompt={p.prompt} note={p.note} />
          ))}
        </div>
      </Section>

      <Section eyebrow="Inspect" title="How to test in MCPJam Inspector">
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 14, lineHeight: "22px" }}>
          The hosted{" "}
          <a href={`https://app.mcpjam.com/?serverUrl=${encodeURIComponent(MCP_URL)}`} target="_blank" rel="noreferrer" style={{ color: "var(--accent-fg)", textDecoration: "underline" }}>
            MCPJam Inspector
          </a>{" "}
          pre-loads the URL via the <code style={{ fontFamily: "var(--font-mono)" }}>?serverUrl=</code> query param —
          useful for poking <code style={{ fontFamily: "var(--font-mono)" }}>tools/list</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>tools/call</code>, and{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>resources/read</code> directly.
        </p>
      </Section>

      <div style={{ paddingTop: 8, borderTop: "1px solid var(--line-1)", display: "flex", gap: 16, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-4)" }}>
        <Link to="/privacy" style={{ color: "var(--fg-4)" }}>privacy</Link>
        <Link to="/terms" style={{ color: "var(--fg-4)" }}>terms</Link>
      </div>
    </div>
  );
}
